<template>
  <LoginSignupContainer>
    <div>
      <FriedParagraph class="branding">{{ $t('create-account') }}</FriedParagraph>
      <FriedH2 class="verify-user-title branding">{{ $t('welcome-to-getaccept') }}</FriedH2>
      <FriedMessage v-if="error" class="error" :title="$t(error.title)" :type="MessageType.Error">
        <i18n-t :keypath="error.description">
          <template #contactSupportLink>
            <FriedLink :normal="true" href="http://help.getaccept.com/" target="_blank">
              {{ $t('contact-support') }}
            </FriedLink>
          </template>
        </i18n-t>
      </FriedMessage>
      <VerifyUserForm
        :loading="loading"
        :default-language="languageStore.language"
        :default-first-name="firstName"
        :default-last-name="lastName"
        :is-invitation="isInvitation"
        @language-change="handleLanguageChange"
        @submit="handleSubmit"
      />
      <FriedParagraph class="sign-in-link">
        {{ $t('already-have-an-account') }}
        <FriedLink href="/" class="bold">{{ $t('sign-in') }}</FriedLink>
      </FriedParagraph>
    </div>
  </LoginSignupContainer>
</template>

<script lang="ts">
import LoginSignupContainer from '@getaccept/lib-shared-new/src/login-signup/components/LoginSignupContainer.vue';
import type { Language } from '@getaccept/lib-shared-new/src/languages/types/language';
import type { Ref } from 'vue';
import { ref, defineComponent, onMounted } from 'vue';
import { MessageType } from '@getaccept/fried-tofu';
import type { ProfileInformation } from '../types/profile-information';
import type { ErrorMessage } from '../../types/error-message';
import { useLanguageStore } from '../../language/store/language.store';
import { VerifyUserHelper } from '../helpers/verify-user.helper';
import VerifyUserForm from './VerifyUserForm.vue';

export default defineComponent({
  name: 'VerifyUser',
  components: {
    VerifyUserForm,
    LoginSignupContainer,
  },
  props: {
    signupId: { type: String, default: '' },
    signupKey: { type: String, default: '' },
    firstName: { type: String, default: '' },
    lastName: { type: String, default: '' },
    isInvitation: { type: Boolean, default: false },
  },
  setup(props) {
    const loading = ref(false);
    const error: Ref<ErrorMessage> = ref(null);

    const languageStore = useLanguageStore();

    const handleLanguageChange = (language: Language) => languageStore.setLanguage(language.key);

    const handleSubmit = async (profileInformation: ProfileInformation) => {
      try {
        loading.value = true;
        const responseUrl = await VerifyUserHelper.verifyUser(
          profileInformation,
          props.signupId,
          props.signupKey
        );
        window.location.href = responseUrl;
      } catch (e) {
        error.value = e;
        VerifyUserHelper.trackVerifyUserError(error.value);
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      VerifyUserHelper.trackPageLoaded(props.signupId);
    });

    return { languageStore, loading, error, handleLanguageChange, handleSubmit, MessageType };
  },
});
</script>

<style lang="scss" scoped>
.error,
.verify-user-title {
  margin-bottom: var(--spacing-50);
}

.sign-in-link {
  color: var(--text-gray);
}

.bold {
  font-weight: 600;
}
</style>
